/**
 * FullCalendar event interaction handlers for manual scheduling.
 * Handles click/drag selection, event drop, and event resize.
 * Click uses previous duration; drag uses actual dragged duration.
 *
 * Ref: KInterviewScheduler/.../schEventHandlers.ts
 */
import { useCallback, useEffect, useRef } from 'react';
import type FullCalendar from '@fullcalendar/react';
import moment from 'moment-timezone';
import {
  DRAG_THRESHOLD_PX,
  SLOT_DURATION_MINUTES,
  MAX_DURATION_MINUTES,
  calcDurationMinutes,
  snapManualDurationMinutes,
} from '../events';

interface UseCalendarEventHandlersOptions {
  calendarRef: React.RefObject<FullCalendar | null>;
  timezone: string;
  duration: number;
  onSlotSelect: (_slot: { start: string; end: string }) => void;
}

export function useCalendarEventHandlers({
  calendarRef,
  timezone,
  duration,
  onSlotSelect,
}: UseCalendarEventHandlersOptions) {
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);

  // Track mouse down position for click vs drag detection
  useEffect(() => {
    const el = calendarRef.current?.getApi()?.el;
    if (!el) return;

    const handleMouseDown = (e: MouseEvent) => {
      mouseDownPos.current = { x: e.clientX, y: e.clientY };
    };
    el.addEventListener('mousedown', handleMouseDown);
    return () => el.removeEventListener('mousedown', handleMouseDown);
  }, [calendarRef]);

  // Click/drag selection → create or replace booked slot
  const handleSelect = useCallback(
    (info: {
      startStr: string;
      endStr: string;
      jsEvent?: MouseEvent | null;
    }) => {
      const selectedDuration = calcDurationMinutes(info.startStr, info.endStr);

      // Detect click vs drag
      const mouseUp = info.jsEvent
        ? { x: info.jsEvent.clientX, y: info.jsEvent.clientY }
        : null;
      const dx =
        mouseDownPos.current && mouseUp
          ? Math.abs(mouseUp.x - mouseDownPos.current.x)
          : 0;
      const dy =
        mouseDownPos.current && mouseUp
          ? Math.abs(mouseUp.y - mouseDownPos.current.y)
          : 0;
      const wasDragged = dx > DRAG_THRESHOLD_PX || dy > DRAG_THRESHOLD_PX;
      const isClick = !wasDragged && selectedDuration <= SLOT_DURATION_MINUTES;

      // Click: use the configured form duration. Drag: snap the dragged
      // duration to the manual grid (5-min steps below 15, 15-min above)
      // so the user can shrink to 5 min while keeping the familiar grid
      // at normal interview lengths.
      const newDuration = isClick
        ? duration
        : snapManualDurationMinutes(selectedDuration);

      const end = isClick
        ? moment
            .tz(info.startStr, timezone)
            .add(duration, 'minutes')
            .toISOString()
        : moment
            .tz(info.startStr, timezone)
            .add(newDuration, 'minutes')
            .toISOString();

      onSlotSelect({ start: info.startStr, end });
      // Clear FullCalendar's drag-select highlight (`.fc-highlight`).
      // Without this, the dashed selection box stays painted at the
      // click location even after the booked event renders on top —
      // visible as a faint pill-shaped halo "behind" the booked event,
      // particularly noticeable for sub-15-min slots where the booked
      // event's height doesn't fully cover the highlight rectangle.
      // SelfScheduleCalendar already calls this; manual mode never did.
      calendarRef.current?.getApi()?.unselect();
      mouseDownPos.current = null;
    },
    [calendarRef, duration, timezone, onSlotSelect]
  );

  // Drag-move booked event
  const handleEventDrop = useCallback(
    (info: {
      event: { startStr: string; endStr: string };
      revert: () => void;
    }) => {
      const start = moment.tz(info.event.startStr, timezone);
      if (start.isBefore(moment().tz(timezone))) {
        info.revert();
        return;
      }

      const dur = calcDurationMinutes(info.event.startStr, info.event.endStr);
      if (dur <= 0 || dur > MAX_DURATION_MINUTES) {
        info.revert();
        return;
      }

      onSlotSelect({
        start: info.event.startStr,
        end: info.event.endStr,
      });
    },
    [timezone, onSlotSelect]
  );

  // Resize booked event. Snap the resulting duration to the manual
  // grid so the user can land cleanly on 5/10/15/30/45/… minute
  // lengths regardless of where FC's snap (5-min) released the handle.
  const handleEventResize = useCallback(
    (info: {
      event: { startStr: string; endStr: string };
      revert: () => void;
    }) => {
      const rawDur = calcDurationMinutes(
        info.event.startStr,
        info.event.endStr
      );
      if (rawDur <= 0) {
        info.revert();
        return;
      }
      const snapped = snapManualDurationMinutes(rawDur);
      const end = moment
        .tz(info.event.startStr, timezone)
        .add(snapped, 'minutes')
        .toISOString();
      onSlotSelect({ start: info.event.startStr, end });
    },
    [onSlotSelect, timezone]
  );

  return { handleSelect, handleEventDrop, handleEventResize };
}
